import React, { useMemo } from 'react';
import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import { useFetchBrandCampaigns } from '@retail/campaign/data-access';
import { BrandCampaign, BrandCampaignDTO, CampaignFilterType } from '@retail/campaign/types';
import { getBrandCampaignColumns, getCampaignBaseColumns } from '@retail/campaign/components';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { GridColDef } from '@mui/x-data-grid';
import { useCampaignsSearchParams } from '@retail/campaign/context';
import { Action } from '@retail/auth/types';

interface ChildrenProps {
  campaigns: BrandCampaign[];
  isLoading: boolean;
  status: 'error' | 'success' | 'loading';
  campaignsTableColumns: GridColDef[];
  availableFilters: CampaignFilterType[];
  campaignActions: Action[];
}

interface Props {
  children: (props: ChildrenProps) => React.ReactNode;
}

const availableBrandCampaignFilters: CampaignFilterType[] = [
  'datePreset',
  'mandatory',
  'campaignType',
  'brands',
  'search',
];

export const BrandCampaignsWrapper = ({ children }: Props) => {
  const { userCanUpdate, userCanDelete, userPermissions } = usePermissionsForCurrentUser();
  const canEditCampaign = userCanUpdate('CAMPAIGN');
  const canDeleteCampaign = userCanDelete('CAMPAIGN');
  const campaignActions = userPermissions.find((p) => p.resource === 'CAMPAIGN')?.actions ?? [];

  const t = useCampaignTFunction();

  const searchParams = useCampaignsSearchParams(availableBrandCampaignFilters);

  const campaignsTableColumns = useMemo(() => {
    const campaignBaseColumns = getCampaignBaseColumns(t);
    const brandCampaignColumns = getBrandCampaignColumns(t);
    return [
      campaignBaseColumns.campaignNameColumn,
      brandCampaignColumns.statusColumn,
      brandCampaignColumns.brandsColumn,
      campaignBaseColumns.campaignTypeColumn,
      campaignBaseColumns.mandatoryColumn,
      campaignBaseColumns.itemCountColumn,
      campaignBaseColumns.descriptionColumn,
      campaignBaseColumns.validFromValidToColumn,
      campaignBaseColumns.purchasePeriodFromPurchasePeriodToColumn,
      campaignBaseColumns.lastUpdatedColumn,
      brandCampaignColumns.updatedByColumn,
    ];
  }, [t]);

  const {
    data: campaignDtos = [],
    isFetching: isLoading,
    status,
  } = useFetchBrandCampaigns({
    ...searchParams,
    suspense: false,
  });

  const campaigns = useMemo(
    () =>
      BrandCampaign.fromDtoList({
        dtos: campaignDtos as BrandCampaignDTO[],
        canEdit: canEditCampaign,
        canDelete: canDeleteCampaign,
      }),
    [campaignDtos, canDeleteCampaign, canEditCampaign]
  );
  return children({
    campaigns,
    isLoading,
    status,
    campaignsTableColumns,
    availableFilters: availableBrandCampaignFilters,
    campaignActions,
  });
};
